import './schedule-appointment-modal.scss';

import { useMutation } from '@apollo/client';
import { isEmpty, kebabCase, trim } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { InputHonorificBlockMinutesController } from '../../../../compositions/form/controllers/input-honorific-block-minutes-controller/input-honorific-block-minutes-controller';
import { useAppContext } from '../../../../data/context/app-context';
import {
  AgendaAppointmentType,
  useAgendaAppointmentTypeValues,
} from '../../../../data/data/agenda/select-values';
import { ChatMessageActionType } from '../../../../data/data/enums';
import { useHealthProfessionalItems } from '../../../../data/data/health-professionals/use-health-professional-items';
import { useAvailableAppointmentHonorificBlockMinuteValues } from '../../../../data/data/honorific-block-times/use-available-appointment-honorific-block-minute-values';
import locales from '../../../../data/translations/locales.json';
import {
  ConsultType,
  ConversationChatMessageItemDataFragment,
  ConversationItemDataFragment,
  CreateConversationChatActionMessageItemDocument,
  DialogFlowType,
  Honorific,
  MessageActionType,
} from '../../../../data/types/api/graphql';
import { formatDate } from '../../../../data/utils/date.utils';
import { useFormatters } from '../../../../data/utils/formatters.utils';
import { AlertMessageType } from '../../../alert-message/alert-message';
import { Button, ButtonIcon, ButtonType } from '../../../button/button';
import { InputCheckboxController } from '../../../form/controllers/input-checkbox';
import { InputSelectController } from '../../../form/controllers/input-select';
import { InputTextAreaController } from '../../../form/controllers/input-textarea';
import { FormRow } from '../../../form/form';
import { CustomFormattedMessage } from '../../../intl/custom-formatted-message';
import { Modal, ModalWidth } from '../../../modal/modal';
import { ModalPrompt } from '../../../modal/modal-prompt';
import { Tabs } from '../../../tabs/tabs';
import { Tooltip } from '../../../tooltip/tooltip';
import { CopyText } from '../../../utils/copy-text/copy-text';
import { useScheduleAppointmentModalForm } from './form/use-schedule-appointment-modal-form';

type ScheduleAppointmentModalProps = {
  type: ChatMessageActionType;
  healthProviderId: string | null | undefined;
  conversationItem: ConversationItemDataFragment | null | undefined;
  onSaved?: (message: ConversationChatMessageItemDataFragment) => void;
  onCancel?: () => void;
  onClose?: () => void;
};

const formatMessage = (
  message: string,
  values: Record<string, string>
): string => message.replace(/{(\w+)}/g, (_, key) => values[key] || '');

const onLoadTranslations = (value: string) =>
  import(`../../../../data/translations/${value}.json`);

export function ScheduleAppointmentModal({
  healthProviderId,
  conversationItem,
  onSaved,
  onCancel,
  onClose,
  ...props
}: ScheduleAppointmentModalProps) {
  const intl = useIntl();

  const { user, showAlertMessage } = useAppContext();

  const { form, formIsValid } = useScheduleAppointmentModalForm();

  const { getFullName, getFullNameAndHonorific, getBirthDate } =
    useFormatters();

  const [selectedLanguage, setSelectedLanguage] = useState<string>('nl');

  useEffect(() => {
    setSelectedLanguage(
      conversationItem?.patient?.language &&
        locales
          .map(l => l.code)
          .includes(conversationItem?.patient?.language.toLocaleLowerCase())
        ? conversationItem?.patient?.language?.toLocaleLowerCase()
        : 'nl'
    );
  }, [conversationItem?.patient?.language]);

  const getDefaultMessage = useCallback(async () => {
    // get key
    const translationKey = `conversation.schedule-appointment.${kebabCase(
      props.type
    )}.content`;

    // check patient language fallback to dutch

    // default messages from locale
    let messages = intl.messages as { [key: string]: string };

    // load locale

    if (intl.locale !== selectedLanguage) {
      const { default: patientTranslations } = await onLoadTranslations(
        selectedLanguage
      );
      messages = patientTranslations as { [key: string]: string };
    }

    // get message
    const message = messages[translationKey];

    if (!message) {
      return '';
    }
    const result = trim(
      formatMessage(message, {
        patient: getFullName(conversationItem?.patient),
        healthProfessional: getFullName(user),
      })
    );
    return result;
  }, [
    conversationItem?.patient,
    getFullName,
    intl.locale,
    intl.messages,
    props.type,
    selectedLanguage,
    user,
  ]);

  useEffect(() => {
    (async () => {
      const d = await getDefaultMessage();
      form.reset({
        type: props.type,
        consultTypes: [ConsultType.Physical],
        text: d,
      });
    })();
  }, [getDefaultMessage, form, props.type]);

  const type = form.watch('type');

  const { data: appointmentTypeData } = useAgendaAppointmentTypeValues(
    AgendaAppointmentType.SIMPLE
  );

  const honorifics = form.watch('honorifics');
  const healthProfessionalId = form.watch('healthProfessionalId');
  const consultTypes = form.watch('consultTypes');

  useEffect(() => {
    form.setValue('healthProfessionalId', '', {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [form]);

  // reset healthproffesional if multiple honorifics are selected
  useEffect(() => {
    if (honorifics && honorifics?.length > 1 && healthProfessionalId) {
      form.setValue('healthProfessionalId', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [form, healthProfessionalId, honorifics]);

  // reset honorifics if no consulttypes are selected
  useEffect(() => {
    if (
      !consultTypes ||
      (consultTypes.length === 0 && honorifics && honorifics.length > 0)
    ) {
      form.setValue('honorifics', undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [consultTypes, form, honorifics]);

  const {
    data: availableAppointmentHonorificBlockMinuteData,
    loading: availableAppointmentHonorificBlockMinuteLoading,
  } = useAvailableAppointmentHonorificBlockMinuteValues({
    healthProviderId,
    internalAgenda: false,
    consultTypes: consultTypes as ConsultType[],
  });

  const { data: healthProfessionalItemsData } = useHealthProfessionalItems({
    healthProviderId,
    honorifics: honorifics ? (honorifics.map(x => x.key) as Honorific[]) : [],
    onlyAgenda: true,
  });

  // TODO COMBINE LOGIC WITH REMINDERS AND BULK
  // get availability of selected healthprofessional
  const {
    data: healthProfessionalAvailablilty,
    loading: healthProfessionalAvailabliltyLoading,
  } = useAvailableAppointmentHonorificBlockMinuteValues(
    {
      healthProviderId,
      internalAgenda: false,
      healthProfessionalId, // selected health professional
      consultTypes: consultTypes as ConsultType[],
    },
    healthProfessionalId === ''
  );

  const isSelectedHealthProfessionalAvailable = useMemo(() => {
    // No availability data - return false
    if (
      !healthProfessionalAvailablilty ||
      healthProfessionalAvailablilty.length === 0
    ) {
      return false;
    }

    // No honorifics selected - return false
    if (!honorifics || honorifics.length === 0) {
      return false;
    }

    // For each selected honorific, check if the health professional has available blocks
    return honorifics.some(selectedHonorific => {
      // Find matching honorific in availability data
      const availabilityForHonorific = healthProfessionalAvailablilty.find(
        availableItem => availableItem.key === selectedHonorific.key
      );

      if (!availabilityForHonorific?.values) {
        return false;
      }

      // Find the value matching the selected honorific value and check if it's available
      return availabilityForHonorific.values.some(
        timeSlot =>
          timeSlot.value === selectedHonorific.value &&
          timeSlot.available === true
      );
    });
  }, [honorifics, healthProfessionalAvailablilty]);

  const { healthProfessionalItem, healthProfessionalItems } = useMemo(
    () => ({
      healthProfessionalItem: (healthProfessionalItemsData?.items ?? []).find(
        option => option.id === healthProfessionalId
      ),
      healthProfessionalItems: healthProfessionalItemsData?.items ?? [],
    }),
    [healthProfessionalId, healthProfessionalItemsData]
  );

  const [
    createConversationChatActionMessageItem,
    { loading: createConversationChatActionMessageItemLoading },
  ] = useMutation(CreateConversationChatActionMessageItemDocument);

  const onHandleSubmit = useCallback(() => {
    form.handleSubmit(async () => {
      const values = form.getValues();

      const getAppointment = () => {
        let parameters: {
          action: MessageActionType;
          dialogFlowType: DialogFlowType | null;
          consultTypes: ConsultType[];
          honorifics: Honorific[];
          healthProfessionalId: string | null;
        } = {
          action: MessageActionType.Chat,
          dialogFlowType: null,
          consultTypes: [],
          honorifics: [],
          healthProfessionalId: null,
        };

        if (values.type === ChatMessageActionType.URINE_QUESTIONNAIRE) {
          parameters = {
            ...parameters,
            action: MessageActionType.StartAnamnesis,
            dialogFlowType: DialogFlowType.Uwi,
          };
        }

        if (values.type === ChatMessageActionType.EXPLAIN_COMPLAINT) {
          parameters = {
            ...parameters,
            action: MessageActionType.StartAnamnesis,
            dialogFlowType: DialogFlowType.Anamnesis,
          };
        }

        if (values.type === ChatMessageActionType.APPOINTMENT) {
          parameters = {
            ...parameters,
            action: MessageActionType.StartAppointment,
            dialogFlowType: DialogFlowType.Anamnesis,
            consultTypes: values.consultTypes as ConsultType[],
            honorifics: values.honorifics?.map(x => x.key) as Honorific[],
            healthProfessionalId: !isEmpty(values.healthProfessionalId)
              ? values.healthProfessionalId
              : null,
          };
        }

        if (values.type === ChatMessageActionType.APPOINTMENT_MISSED) {
          parameters = {
            ...parameters,
            action: MessageActionType.MissedAppointment,
          };
        }

        if (values.type === ChatMessageActionType.APPOINTMENT_NO_SHOW) {
          parameters = {
            ...parameters,
            action: MessageActionType.NoShowProtocol,
          };
        }

        return parameters;
      };

      try {
        const result = await createConversationChatActionMessageItem({
          variables: {
            honorificBlockMinutes: values.honorifics
              ? values.honorifics?.map(item => ({
                  blockMinutes: item.value,
                  honorific: item.key,
                }))
              : [],
            conversationId: conversationItem?.id,
            text: values.text ?? '',
            ...getAppointment(),
          },
        });

        if (result.data?.messageActionCreate?.message) {
          onSaved?.(result.data?.messageActionCreate?.message);

          onClose?.();
        } else {
          showAlertMessage({
            type: AlertMessageType.ERROR,
            message: intl.formatMessage({
              id: 'common.error',
            }),
          });
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    })();
  }, [
    intl,
    form,
    conversationItem,
    createConversationChatActionMessageItem,
    showAlertMessage,
    onSaved,
    onClose,
  ]);

  const [cancelPromptOpen, setCancelPromptOpen] = useState<boolean>(false);

  const onHandleCancel = useCallback(
    (confirmed: boolean = false) => {
      if (!confirmed && form.formState.isDirty) {
        setCancelPromptOpen(true);
      } else {
        onCancel?.();
        onClose?.();
      }
    },
    [form.formState.isDirty, onCancel, onClose]
  );

  return (
    <>
      <Modal
        title={
          <CustomFormattedMessage
            id={`conversation.schedule-appointment.${kebabCase(
              props.type
            )}.title`}
          />
        }
        width={ModalWidth.LARGE}
        onClose={onHandleCancel}
        buttons={
          <>
            <Button
              type={ButtonType.SECONDARY}
              inverted
              onClick={() => onHandleCancel(false)}
            >
              <CustomFormattedMessage id="common.cancel" />
            </Button>

            <Button
              icon={
                createConversationChatActionMessageItemLoading
                  ? ButtonIcon.LOADING
                  : ButtonIcon.PAPERPLANE
              }
              disabled={
                !formIsValid ||
                createConversationChatActionMessageItemLoading ||
                (type === ChatMessageActionType.APPOINTMENT &&
                  !honorifics?.length)
              }
              onClick={onHandleSubmit}
            >
              <CustomFormattedMessage id="conversation.chat-message-create.save" />
            </Button>
          </>
        }
      >
        <FormProvider {...form}>
          {type === ChatMessageActionType.APPOINTMENT ? (
            <>
              <FormRow>
                <div className="chat-message-patient">
                  <b>{getFullName(conversationItem?.patient)}</b>
                  <p>
                    <CopyText
                      text={formatDate(
                        conversationItem?.patient.birthDate,
                        'dd-mm-YYYY'
                      )}
                    >
                      {getBirthDate(conversationItem?.patient)}
                    </CopyText>
                  </p>
                </div>
              </FormRow>

              <FormRow>
                <InputCheckboxController<ConsultType>
                  name="consultTypes"
                  label={
                    <>
                      <CustomFormattedMessage id="common.consult-types" />
                      <small>*</small>
                    </>
                  }
                  options={appointmentTypeData}
                  multiple
                />
              </FormRow>

              <FormRow>
                {!availableAppointmentHonorificBlockMinuteLoading &&
                  consultTypes.length > 0 && (
                    <InputHonorificBlockMinutesController
                      label={
                        <>
                          <CustomFormattedMessage id="common.honorific" />
                          <small>*</small>
                        </>
                      }
                      name="honorifics"
                      options={availableAppointmentHonorificBlockMinuteData}
                    />
                  )}
                {availableAppointmentHonorificBlockMinuteLoading && (
                  <CustomFormattedMessage id="common.loading" />
                )}
                {consultTypes.length === 0 && (
                  <CustomFormattedMessage id="agenda.appointment-create.no-consult-type" />
                )}
              </FormRow>

              <FormRow>
                <InputSelectController<string>
                  className="chat-message-health-professional"
                  name="healthProfessionalId"
                  label={
                    <>
                      {!healthProfessionalAvailabliltyLoading &&
                        healthProfessionalId &&
                        isSelectedHealthProfessionalAvailable === false && (
                          <Tooltip
                            message={
                              <CustomFormattedMessage id="common.honorific-block-minutes-not-available" />
                            }
                          >
                            <i className="icon info" />
                          </Tooltip>
                        )}
                      <CustomFormattedMessage id="common.health-professional" />
                    </>
                  }
                  placeholder={intl.formatMessage({
                    id: 'common.health-professional-placeholder',
                  })}
                  value={getFullName(healthProfessionalItem)}
                  options={healthProfessionalItems.map(option => ({
                    key: option.id ?? '',
                    title: getFullNameAndHonorific(option),
                  }))}
                  disabled={!honorifics?.length || honorifics?.length > 1}
                />
              </FormRow>

              <FormRow>
                <InputTextAreaController
                  name="text"
                  label={
                    <CustomFormattedMessage id="conversation.schedule-appointment.message" />
                  }
                  description={
                    conversationItem?.patient ? (
                      <CustomFormattedMessage
                        id="conversation.schedule-appointment.message-description"
                        values={{
                          language: (
                            <CustomFormattedMessage
                              id={`common.language.${kebabCase(
                                conversationItem.patient.language
                              )}`}
                            />
                          ),
                        }}
                      />
                    ) : undefined
                  }
                />
              </FormRow>
            </>
          ) : (
            <FormRow>
              <Tabs<string>
                value={selectedLanguage}
                items={locales.map(x => ({
                  key: x.code,
                  title: x.name,
                }))}
                onChange={value => {
                  setSelectedLanguage(value);
                }}
              />

              <InputTextAreaController
                name="text"
                label={
                  <CustomFormattedMessage id="conversation.schedule-appointment.default-message" />
                }
                description={
                  conversationItem?.patient ? (
                    <CustomFormattedMessage
                      id="conversation.schedule-appointment.default-message-description"
                      values={{
                        language: (
                          <CustomFormattedMessage
                            id={`common.language.${kebabCase(
                              conversationItem.patient.language
                            )}`}
                          />
                        ),
                      }}
                    />
                  ) : undefined
                }
                className="chat-message-default-text"
              />
            </FormRow>
          )}
        </FormProvider>
      </Modal>

      {cancelPromptOpen && (
        <ModalPrompt
          onConfirm={() => onHandleCancel(true)}
          onClose={() => setCancelPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage id="common.confirm-not-saved-changes" />
          </p>
        </ModalPrompt>
      )}
    </>
  );
}
